import React, { useState, useEffect } from 'react';
import qs from 'qs';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Button,
  Divider,
  Paper,
  TextField,
  Box,
  FormControl,
  InputLabel,
  NativeSelect,
  Collapse
} from '@material-ui/core';
import styled from 'styled-components';
import Filter from './Filter';
import { offerCode } from '../../../utils/bindings/offerCode';
import { searchCommands } from '../commandsActions';
import { exportCommandsReport } from '../../exports/exportsActions';

const CustomDivider = styled(Divider)`
  width: 1px;
  height: 40px;
  margin: 4px;
`;

const CustomPaper = styled(Paper)`
  padding: 2px 4px 32px 4px;
`;

/**
 * Barre de recherche
 * @param {Function} setPage     Mettre à jour la page
 * @param {Function} setQuery    Mettre à jour les query de la requête
 * @param {String}   initParams  Query d'initialisation
 */
const Search = ({
  initParams,
  filters,
  dispatchSearchCommands,
  dispatchExportCommandsReport,
  updateCommandWatch,
  courseWatch,
  mergeCommandWatch,
  mandatoryStates = [],
  usedCarrier = []
}) => {
  const defaultParams = {
    query: '',
    states: [],
    getAll: 0,
    page: 1,
    maxResults: 8,
    ...initParams
  };

  const history = useHistory();
  const { search: locationSearch } = useLocation();
  const [init, setInit] = useState(false);
  const [showMoreFilter, setShowMoreFilter] = useState(false);

  const getUrlParams = () =>
    qs.parse(locationSearch, { ignoreQueryPrefix: true });

  /**
   * Initialisation de l'url
   */
  useEffect(() => {
    const params = {
      ...defaultParams,
      ...getUrlParams()
    };
    history.replace({
      search: qs.stringify(params)
    });
    setInit(true);
  }, [locationSearch]);

  useEffect(() => {
    if (!init) {
      return;
    }
    const currentParams = getUrlParams();

    const params = {
      ...defaultParams,
      ...currentParams
    };
    dispatchSearchCommands({
      ...params,
      states:
        !params.states.length && mandatoryStates.length
          ? mandatoryStates
          : params.states
    });
  }, [
    getUrlParams().page,
    dispatchSearchCommands,
    init,
    updateCommandWatch,
    courseWatch,
    mergeCommandWatch
  ]);

  /**
   * Modification de la donnée inputText utiliser
   * dans la query. Pour rechercher dans les commandes
   * @param  {Object} event event
   */
  const handleChange = event => {
    history.replace({
      search: qs.stringify({
        ...getUrlParams(),
        [event.target.name]: event.target.value
      })
    });
  };

  /**
   * Function exécuté lors du clique sur l'icone de recherche
   */
  const search = () => {
    history.replace({
      search: qs.stringify({
        ...getUrlParams(),
        page: 1
      })
    });
    const currentParams = getUrlParams();
    const params = {
      ...defaultParams,
      ...currentParams
    };
    dispatchSearchCommands({
      ...params,
      states:
        !params.states.length && mandatoryStates.length
          ? mandatoryStates
          : params.states
    });
  };

  /**
   * Function exécuté lors du clique sur la touche Enter
   */
  const keyPressed = event => {
    if (event.key === 'Enter') {
      search();
    }
  };

  /**
   * Function permettant de reinitialiser tous les filtres de recherche,
   * elle remet aussi la pagination du tableau à 0
   */
  const reset = () => {
    history.replace({
      search: qs.stringify(defaultParams)
    });
    const params = {
      ...defaultParams
    };
    dispatchSearchCommands({
      ...params,
      states: [...params.states, ...mandatoryStates]
    });
  };

  const exportCommands = () => {
    const currentParams = getUrlParams();
    const params = {
      ...defaultParams,
      ...currentParams
    };

    delete params.page;
    delete params.maxResults;

    if (params.states && !Array.isArray(params.states)) {
      params.states = [params.states];
    }

    dispatchExportCommandsReport({
      ...params,
      states:
        !params.states.length && mandatoryStates.length
          ? mandatoryStates
          : params.states,
      format: 'excel'
    });
  };

  const handleChangeFilter = event => {
    const { value } = event.target;
    history.replace({
      search: qs.stringify({
        ...getUrlParams(),
        states: value,
        page: 1
      })
    });
    const currentParams = getUrlParams();
    const params = {
      ...defaultParams,
      ...currentParams,
      states: value
    };
    dispatchSearchCommands({
      ...params,
      states:
        !params.states.length && mandatoryStates.length
          ? mandatoryStates
          : params.states
    });
  };

  return (
    <CustomPaper>
      <Box display="flex" justifyContent="center" alignItems="center">
        <Filter handleChange={handleChangeFilter} filters={filters} />
        <TextField
          style={{ width: '50%', marginRight: 24 }}
          id="query"
          name="query"
          label="Recherche"
          value={
            qs.parse(locationSearch, { ignoreQueryPrefix: true }).query || ''
          }
          onChange={handleChange}
          onKeyPress={keyPressed}
        />
        <CustomDivider />
        <Button
          color="primary"
          variant="contained"
          disabled={'query' === ''}
          style={{ marginRight: 30, marginLeft: 30 }}
          onClick={search}
        >
          Rechercher
        </Button>
        <Button
          color="secondary"
          variant="contained"
          disabled={'query' === ''}
          style={{ marginRight: 30, marginLeft: 30 }}
          onClick={reset}
        >
          Réinitialiser
        </Button>
        <Button
          color="primary"
          variant="outlined"
          style={{ marginRight: 30, marginLeft: 30 }}
          onClick={exportCommands}
        >
          Export
        </Button>
      </Box>
      <Box
        onClick={() => setShowMoreFilter(!showMoreFilter)}
        display="flex"
        justifyContent="end"
        mr={4}
        mt={2}
        mb={1}
        style={{ cursor: 'pointer' }}
        color="blue"
      >
        {showMoreFilter ? '- d’options' : '+ d’options'}
      </Box>
      <Collapse in={showMoreFilter}>
        <Box display="flex" ml={3}>
          <TextField
            style={{ margin: '0px 24px', width: 200 }}
            id="recipientQuery"
            name="recipientQuery"
            label="Destinataire"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .recipientQuery || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
          />
          <TextField
            style={{ margin: '0px 24px', width: 200 }}
            id="externalReferences"
            name="externalReferences"
            label="Référence de commande"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .externalReferences || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
          />
          <TextField
            style={{ margin: '0px 24px', width: 200 }}
            id="itemExternalReferences"
            name="itemExternalReferences"
            label="Référence de produit"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .itemExternalReferences || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
          />
          <TextField
            style={{ margin: '0px 24px', width: 200 }}
            id="itemDescription"
            name="itemDescription"
            label="Description Produit"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .itemDescription || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
          />
        </Box>
        <Box display="flex" ml={3} mt={2}>
          <TextField
            style={{ margin: '0px 24px', width: 200 }}
            id="trackingNumbers"
            name="trackingNumbers"
            label="N° de suivi"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .trackingNumbers || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
          />

          <FormControl style={{ margin: '0px 24px' }}>
            <InputLabel>Offre de livraison</InputLabel>
            <NativeSelect
              style={{ width: 200 }}
              name="carrierOfferId"
              onChange={handleChange}
              value={
                qs.parse(locationSearch, { ignoreQueryPrefix: true })
                  .carrierOfferId || ''
              }
            >
              <option aria-label="None" value="" />
              {offerCode.map(data => (
                <option key={data.id} value={data.value}>
                  {data.name}
                </option>
              ))}
            </NativeSelect>
          </FormControl>

          <TextField
            style={{ margin: '0px 24px', width: 200 }}
            id="itemEan13List"
            name="itemEan13List"
            label="EAN 13"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .itemEan13List || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
          />
          <TextField
            style={{ margin: '0px 24px', width: 200 }}
            id="imeis"
            name="imeis"
            label="IMEI"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true }).imeis || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
          />
        </Box>
        <Box display="flex" ml={3} mt={2}>
          <TextField
            style={{ margin: '0px 24px', width: 200 }}
            id="operationNumber"
            name="operationNumber"
            label="ID Opération"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .operationNumber || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
          />

          <FormControl style={{ margin: '0px 24px' }}>
            <InputLabel>Offre de livraison</InputLabel>
            <NativeSelect
              style={{ width: 200 }}
              name="carrierId"
              onChange={handleChange}
              value={
                qs.parse(locationSearch, { ignoreQueryPrefix: true })
                  .carrierId || ''
              }
            >
              <option aria-label="None" value="" />
              {usedCarrier.map(data => (
                <option key={data.id} value={data.id}>
                  {data.name}
                </option>
              ))}
            </NativeSelect>
          </FormControl>
        </Box>
        <Box display="flex" ml={3} mt={2}>
          <TextField
            style={{ width: 220, marginLeft: 24 }}
            id="createdDateFrom"
            name="createdDateFrom"
            label="Date de création minimum"
            type="date"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .createdDateFrom || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            style={{ width: 220, marginLeft: 24 }}
            id="createdDateTo"
            name="createdDateTo"
            label="Date de création maximum"
            type="date"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .createdDateTo || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            style={{ width: 220, marginLeft: 24 }}
            id="packedDateFrom"
            name="packedDateFrom"
            label="Date d'emballage minimum"
            type="date"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .packedDateFrom || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            style={{ width: 220, marginLeft: 24 }}
            id="packedDateTo"
            name="packedDateTo"
            label="Date d'emballage maximum"
            type="date"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .packedDateTo || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
            InputLabelProps={{
              shrink: true
            }}
          />
        </Box>
        <Box display="flex" ml={3} mt={2}>
          <TextField
            style={{ width: 220, marginLeft: 24 }}
            id="shippedDateFrom"
            name="shippedDateFrom"
            label="Date d'expédition minimum"
            type="date"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .shippedDateFrom || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            style={{ width: 220, marginLeft: 24 }}
            id="shippedDateTo"
            name="shippedDateTo"
            label="Date d'expédition maximum"
            type="date"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .shippedDateTo || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            style={{ width: 220, marginLeft: 24 }}
            id="deliveredDateFrom"
            name="deliveredDateFrom"
            label="Date de livraison minimum"
            type="date"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .deliveredDateFrom || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            style={{ width: 220, marginLeft: 24 }}
            id="deliveredDateTo"
            name="deliveredDateTo"
            label="Date de livraison maximum"
            type="date"
            value={
              qs.parse(locationSearch, { ignoreQueryPrefix: true })
                .deliveredDateTo || ''
            }
            onChange={handleChange}
            onKeyPress={keyPressed}
            InputLabelProps={{
              shrink: true
            }}
          />
        </Box>
      </Collapse>
    </CustomPaper>
  );
};

const mapDispatchToProps = dispatch => ({
  dispatchSearchCommands: params => dispatch(searchCommands(params)),
  dispatchExportCommandsReport: params => dispatch(exportCommandsReport(params))
});

const mapStateToProps = state => ({
  updateCommandWatch: state.commands.updateCommandWatch,
  courseWatch: state.course.courseWatch,
  mergeCommandWatch: state.mergeCommands.mergeCommandWatch,
  usedCarrier: state.init.usedCarrier
});

Search.propTypes = {
  initParams: PropTypes.object.isRequired,
  filters: PropTypes.array.isRequired,
  dispatchSearchCommands: PropTypes.func.isRequired,
  dispatchExportCommandsReport: PropTypes.func.isRequired,
  mandatoryStates: PropTypes.array,
  usedCarrier: PropTypes.array,
  updateCommandWatch: PropTypes.bool.isRequired,
  courseWatch: PropTypes.bool.isRequired,
  mergeCommandWatch: PropTypes.bool.isRequired
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Search);
